import api from '../api/axiosInstance';

/**
 * Profile Service
 * 
 * Handles profile validation and checks for plan/subscription purchases
 * with real-time polling support
 */

/**
 * Check if user profile is complete
 * 
 * Required fields for plan purchase:
 * - first_name
 * - last_name
 * - email_address
 * - business_name
 * - phone_number
 * - address
 * - city
 * - state
 * - zipcode
 * - country
 * 
 * @param {string} businessUserId - The business user ID
 * @returns {Promise<{isComplete: boolean, missingFields: string[], profileData: object}>}
 */
export const checkProfileCompletion = async (businessUserId) => {
  try {
    console.log('[ProfileService] Checking profile completion for user:', businessUserId);

    // Fetch user profile
    const response = await api.get(`/business/store/info?business_id=${businessUserId}`);

    if (response.data.status !== 'success') {
      console.error('[ProfileService] Failed to fetch profile:', response.data);
      return {
        isComplete: false,
        missingFields: ['all'],
        profileData: null,
        error: 'Failed to fetch profile data'
      };
    }

    const result = response.data.result;
    const contactInfo = result.contact_info || {};

    // Map profile data
    const profileData = {
      first_name: result.first_name || '',
      last_name: result.last_name || '',
      email_address: contactInfo.email || '',
      business_name: result.business_name || '',
      phone_number: contactInfo.phone || '',
      address: contactInfo.address || '',
      city: contactInfo.city || '',
      state: contactInfo.state || '',
      zipcode: contactInfo.zipcode || '',
      country: contactInfo.country || 'India'
    };

    // Required fields for plan purchase
    const requiredFields = [
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email_address', label: 'Email Address' },
      { key: 'business_name', label: 'Business Name' },
      { key: 'phone_number', label: 'Phone Number' },
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'state', label: 'State' },
      { key: 'zipcode', label: 'ZIP Code' }
    ];

    // Check for missing fields
    const missingFields = requiredFields
      .filter(field => !profileData[field.key] || profileData[field.key].trim() === '')
      .map(field => field.label);

    const isComplete = missingFields.length === 0;

    console.log('[ProfileService] Profile check result:', {
      isComplete,
      missingFieldsCount: missingFields.length,
      missingFields
    });

    return {
      isComplete,
      missingFields,
      profileData
    };
  } catch (error) {
    console.error('[ProfileService] Error checking profile completion:', {
      error: error.message,
      status: error.response?.status,
      data: error.response?.data
    });

    return {
      isComplete: false,
      missingFields: ['all'],
      profileData: null,
      error: error.message
    };
  }
};

/**
 * Start polling profile completion status
 * 
 * @param {string} businessUserId - The business user ID
 * @param {Function} callback - Callback function to handle profile updates
 * @param {number} interval - Polling interval in milliseconds (default: 30 seconds)
 * @returns {number} - Interval ID for cleanup
 */
export const startProfilePolling = (businessUserId, callback, interval = 30000) => {
  console.log('[ProfileService] Starting profile polling with interval:', interval);
  
  // Initial check
  checkProfileCompletion(businessUserId).then(callback);
  
  // Set up polling
  const intervalId = setInterval(() => {
    checkProfileCompletion(businessUserId).then(callback);
  }, interval);
  
  return intervalId;
};

/**
 * Stop polling profile completion status
 * 
 * @param {number} intervalId - The interval ID to clear
 */
export const stopProfilePolling = (intervalId) => {
  if (intervalId) {
    console.log('[ProfileService] Stopping profile polling');
    clearInterval(intervalId);
  }
};

/**
 * Validate email format
 * @param {string} email 
 * @returns {boolean}
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (10 digits)
 * @param {string} phone 
 * @returns {boolean}
 */
export const isValidPhone = (phone) => {
  return /^\d{10}$/.test(phone);
};

/**
 * Validate ZIP code (6 digits for India)
 * @param {string} zipcode 
 * @returns {boolean}
 */
export const isValidZipcode = (zipcode) => {
  return /^\d{6}$/.test(zipcode);
};

export default {
  checkProfileCompletion,
  startProfilePolling,
  stopProfilePolling,
  isValidEmail,
  isValidPhone,
  isValidZipcode
};
